import React from 'react';
import styled from 'styled-components';
import { Player } from '../../types';
import { dictionaries } from '../../utils/dictionary';
import {
  ModalOverlay,
  ModalCard,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalTitle,
  PhaseButton,
} from '../../styles/SharedStyles';
import { theme } from '../../styles/theme';

interface PlayerPunishmentProps {
  language: number;
  player: Player;
  playerIndex: number;
  currentFouls: number;
  onFoulAction: (action: 'add' | 'reset' | 'disqualify' | 'penalty-today' | 'penalty-tomorrow') => void;
}

const PlayerSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.xl};
`;

const PlayerAvatar = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 3px solid ${theme.colors.border.primary};
  box-shadow: ${theme.shadows.lg};
`;

const PlayerName = styled.h6`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: ${theme.colors.text.primary};
`;

const PlayerMeta = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: ${theme.colors.text.secondary};
`;

const AlertBox = styled.div<{ $variant: 'warning' | 'danger' | 'info' }>`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.lg};
  background: ${({ $variant }) => {
    if ($variant === 'danger') return theme.colors.card.error;
    if ($variant === 'warning') return theme.colors.card.instruction;
    return 'rgba(68, 255, 210, 0.12)';
  }};
  border: 1px solid ${({ $variant }) => {
    if ($variant === 'danger') return 'rgba(255, 77, 109, 0.35)';
    if ($variant === 'warning') return 'rgba(255, 196, 0, 0.35)';
    return 'rgba(68, 255, 210, 0.35)';
  }};
  
  strong {
    display: block;
    margin-bottom: ${theme.spacing.sm};
    font-weight: 700;
    color: ${theme.colors.text.primary};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.md};
  justify-content: center;
`;

const IconSpan = styled.span`
  margin-right: ${theme.spacing.sm};
  font-size: 1.125rem;
`;

const ColoredModalHeader = styled(ModalHeader)<{ $variant: 'warning' | 'danger' }>`
  background: ${({ $variant }) => {
    if ($variant === 'danger') return 'linear-gradient(135deg, rgba(255, 77, 109, 0.25), rgba(200, 50, 80, 0.25))';
    return 'linear-gradient(135deg, rgba(255, 196, 0, 0.25), rgba(200, 150, 0, 0.25))';
  }};
  border-bottom: 1px solid ${({ $variant }) => {
    if ($variant === 'danger') return 'rgba(255, 77, 109, 0.35)';
    return 'rgba(255, 196, 0, 0.35)';
  }};
  padding-bottom: 0.5rem;
`;

const PlayerPunishment: React.FC<PlayerPunishmentProps> = ({
  language,
  player,
  playerIndex,
  currentFouls,
  onFoulAction,
}) => {
  const dict = dictionaries[language].ph;
  const playerNumber = playerIndex + 1;

  const handleAction = (action: 'add' | 'reset' | 'disqualify' | 'penalty-today' | 'penalty-tomorrow') => {
    onFoulAction(action);
  };

  // 4th foul - choice between reset or disqualify
  if (currentFouls === 4) {
    return (
          <ModalOverlay onClick={(e) => e.stopPropagation()}>
        <ModalCard>
          <ColoredModalHeader $variant="danger">
          </ColoredModalHeader>
          
          <ModalBody>
            <PlayerSection>
              <PlayerAvatar
                src={player.avatar}
                alt={dict.player_avatar_alt.replace('{0}', player.nickname)}
              />
              <div style={{ textAlign: 'center' }}>
                <PlayerName>{playerNumber}. {player.nickname}</PlayerName>
                <PlayerMeta>{dict.plgot4f}</PlayerMeta>
              </div>
            </PlayerSection>
            
            <AlertBox $variant="danger">
              <strong>{dict.foul_four_heading}</strong>
              {dict.foul_four_instructions}
            </AlertBox>
          </ModalBody>

          <ModalFooter>
            <ButtonGroup>
              <PhaseButton
                $variant="danger"
                onClick={() => handleAction('disqualify')}
              >
                <IconSpan>❌</IconSpan>
                {dict.disqualify}
              </PhaseButton>
              <PhaseButton
                $variant="secondary"
                onClick={() => handleAction('reset')}
              >
                <IconSpan>🔄</IconSpan>
                {dict.rcnt}
              </PhaseButton>
            </ButtonGroup>
          </ModalFooter>
        </ModalCard>
      </ModalOverlay>
    );
  }

  // 3rd foul - choice of penalty timing
  if (currentFouls === 3) {
    return (
          <ModalOverlay onClick={(e) => e.stopPropagation()}>
        <ModalCard>
          <ColoredModalHeader $variant="warning">
          </ColoredModalHeader>
          
          <ModalBody>
            <PlayerSection>
              <PlayerAvatar
                src={player.avatar}
                alt={dict.player_avatar_alt.replace('{0}', player.nickname)}
              />
              <div style={{ textAlign: 'center' }}>
                <PlayerName>{playerNumber}. {player.nickname}</PlayerName>
                <PlayerMeta>{dict.plgot3f}</PlayerMeta>
              </div>
            </PlayerSection>
            
            <AlertBox $variant="info">
              <strong>{dict.foul_three_heading}</strong>
              {dict.foul_three_instructions}
            </AlertBox>
          </ModalBody>

          <ModalFooter>
            <ButtonGroup>
              <PhaseButton
                $variant="primary"
                onClick={() => handleAction('penalty-today')}
              >
                {dict.tday}
              </PhaseButton>
              <PhaseButton
                $variant="secondary"
                onClick={() => handleAction('penalty-tomorrow')}
              >
                {dict.nday}
              </PhaseButton>
              <PhaseButton
                $variant="outline"
                onClick={() => handleAction('reset')}
              >
                <IconSpan>🔄</IconSpan>
                {dict.rcnt}
              </PhaseButton>
            </ButtonGroup>
          </ModalFooter>
        </ModalCard>
      </ModalOverlay>
    );
  }

  // Should not reach here - only 3rd and 4th fouls should show this component
  return null;
};

export default PlayerPunishment;
